import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { BookOpen, Clock, Award, TrendingUp } from "lucide-react";
import { differenceInDays, parseISO } from "date-fns";

interface SubjectStats {
  subjectId: string;
  subjectName: string;
  totalStudents: number;
  inProgress: number;
  completed: number;
  examsPassed: number;
  examsFailed: number;
  completionRate: number;
  passRate: number;
  avgDaysToComplete: number;
}

const fetchSubjectAnalysis = async (): Promise<SubjectStats[]> => {
  const [subjectsRes, studentSubjectsRes] = await Promise.all([
    supabase.from("subjects").select("id, name"),
    supabase.from("student_subjects").select("subject_id, status, exam_result, start_date, completion_date"),
  ]);

  if (subjectsRes.error) throw subjectsRes.error;
  if (studentSubjectsRes.error) throw studentSubjectsRes.error;

  const subjects = subjectsRes.data || [];
  const studentSubjects = studentSubjectsRes.data || [];

  const stats: SubjectStats[] = [];

  for (const subject of subjects) {
    const subjectData = studentSubjects.filter(ss => ss.subject_id === subject.id);
    
    const inProgress = subjectData.filter(s => s.status === "In Progress").length;
    const completed = subjectData.filter(s => s.status === "Completed").length;
    const examsPassed = subjectData.filter(s => s.exam_result === "Pass").length;
    const examsFailed = subjectData.filter(s => s.exam_result === "Fail").length;
    const totalExams = examsPassed + examsFailed;

    // Calculate average days to complete
    const completedWithDates = subjectData.filter(
      s => s.status === "Completed" && s.start_date && s.completion_date
    );
    const avgDaysToComplete = completedWithDates.length > 0
      ? Math.round(
          completedWithDates.reduce((sum, s) => {
            return sum + differenceInDays(parseISO(s.completion_date!), parseISO(s.start_date!));
          }, 0) / completedWithDates.length
        )
      : 0;

    stats.push({
      subjectId: subject.id,
      subjectName: subject.name,
      totalStudents: subjectData.length,
      inProgress,
      completed,
      examsPassed,
      examsFailed,
      completionRate: subjectData.length > 0 
        ? Math.round((completed / subjectData.length) * 100) 
        : 0,
      passRate: totalExams > 0 
        ? Math.round((examsPassed / totalExams) * 100) 
        : 0,
      avgDaysToComplete,
    });
  }

  return stats.filter(s => s.totalStudents > 0).sort((a, b) => b.totalStudents - a.totalStudents);
};

export const SubjectAnalysisReport = () => {
  const { data: subjectStats = [], isLoading } = useQuery({
    queryKey: ["subject-analysis-report"],
    queryFn: fetchSubjectAnalysis,
  });

  if (isLoading) {
    return <Card><CardContent className="p-6">Loading subject analysis...</CardContent></Card>;
  }

  if (subjectStats.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No subject data available
        </CardContent>
      </Card>
    );
  }

  const chartData = subjectStats.slice(0, 10).map(s => ({
    name: s.subjectName.length > 15 ? s.subjectName.substring(0, 15) + '...' : s.subjectName,
    completed: s.completed,
    inProgress: s.inProgress,
    passRate: s.passRate,
  }));

  const totalStudents = subjectStats.reduce((sum, s) => sum + s.totalStudents, 0);
  const totalCompleted = subjectStats.reduce((sum, s) => sum + s.completed, 0);
  const avgPassRate = subjectStats.length > 0
    ? Math.round(subjectStats.reduce((sum, s) => sum + s.passRate, 0) / subjectStats.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{subjectStats.length}</p>
                <p className="text-xs text-muted-foreground">Total Subjects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{totalStudents}</p>
                <p className="text-xs text-muted-foreground">Enrollments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{totalCompleted}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{avgPassRate}%</p>
                <p className="text-xs text-muted-foreground">Avg Pass Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Subject Progress Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis dataKey="name" type="category" width={100} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Bar dataKey="completed" name="Completed" fill="#22c55e" stackId="a" />
              <Bar dataKey="inProgress" name="In Progress" fill="#3b82f6" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Subject Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {subjectStats.map(subject => (
          <Card key={subject.subjectId}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{subject.subjectName}</CardTitle>
                <Badge variant="secondary">{subject.totalStudents} students</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Completion Rate</span>
                <span className="font-medium">{subject.completionRate}%</span>
              </div>
              <Progress value={subject.completionRate} />
              
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-muted rounded">
                  <p className="font-bold text-lg">{subject.inProgress}</p>
                  <p className="text-muted-foreground">In Progress</p>
                </div>
                <div className="p-2 bg-green-500/10 rounded">
                  <p className="font-bold text-lg text-green-600">{subject.completed}</p>
                  <p className="text-muted-foreground">Completed</p>
                </div>
                <div className="p-2 bg-primary/10 rounded">
                  <p className="font-bold text-lg">{subject.passRate}%</p>
                  <p className="text-muted-foreground">Pass Rate</p>
                </div>
              </div>

              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Exams: {subject.examsPassed} passed / {subject.examsFailed} failed</span>
                {subject.avgDaysToComplete > 0 && (
                  <span>Avg: {subject.avgDaysToComplete} days</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
